import type { ReactNode, RefObject } from 'react';
import { useRef, useState, useEffect } from 'react';
import { throttle } from 'lodash-es';
import { useVirtualizer } from '@tanstack/react-virtual';
import useResizeObserver from 'use-resize-observer';
import { css, styled } from 'goober';
import { Button } from '@/ui/button/button';
import { useTranslation } from 'react-i18next';
import { Flexbox } from '@/ui/flexbox/flexbox';

const BREAKPOINT = {
  xs: 375,
  sm: 640,
  lg: 1024,
};
const ITEM_RATIO = 16 / 9;

const DEFAULT_ITEM_SIZE = 335;

export function VirtualGrid<T>({
  items,
  renderItem,
  estimateItemHeight,
  gap = { x: 16, y: 16 },
  initialLimit,
  incrementBy,
}: {
  items: Array<T>;
  renderItem: (item: T) => ReactNode;
  estimateItemHeight?: number;
  gap?: { x: number; y: number };
  initialLimit?: number;
  incrementBy?: number;
}) {
  const { t } = useTranslation('default');
  const parentRef = useRef<HTMLDivElement>(null);

  const [columns, setColumns] = useState(4);
  const [itemSize, setItemSize] = useState({
    width: DEFAULT_ITEM_SIZE,
    height: estimateItemHeight ?? DEFAULT_ITEM_SIZE,
  });

  const [visibleCount, setVisibleCount] = useState(
    initialLimit ?? items.length,
  );
  const visibleItems = items.slice(0, visibleCount);

  const { width: containerWidth = 0 } = useResizeObserver({
    // use-resize-observer's types predate React 19's `RefObject<T | null>`; the
    // hook handles a null `current` at runtime.
    ref: parentRef as RefObject<HTMLDivElement>,
  });

  const rowVirtualizer = useVirtualizer({
    count: Math.ceil(visibleItems.length / columns),
    getScrollElement: () => parentRef.current,
    estimateSize: () => estimateItemHeight ?? itemSize.height,
    measureElement: (el: HTMLElement) => el.getBoundingClientRect().height,
    overscan: 2,
    gap: gap.y,
    scrollPaddingEnd: 8,
  });

  const columnVirtualizer = useVirtualizer({
    horizontal: true,
    count: columns,
    getScrollElement: () => parentRef.current,
    estimateSize: () => itemSize.width,
    overscan: 2,
    gap: gap.x,
  });

  useEffect(() => {
    rowVirtualizer.measure();
    columnVirtualizer.measure();
  }, [itemSize.height, columns, visibleItems.length]);

  const getColumnsCount = (width: number) => {
    if (width <= BREAKPOINT.xs) return 3;
    if (width <= BREAKPOINT.sm) return 5;
    return 4;
  };

  const getItemWidth = (width: number, columns: number, gapX: number) => {
    return Math.floor((width - (columns - 1) * gapX) / columns);
  };

  const getItemHeight = (width: number, itemWidth: number) => {
    if (width >= BREAKPOINT.sm) return itemWidth / ITEM_RATIO;
    return itemWidth;
  };

  const handleResize = throttle(() => {
    const column = getColumnsCount(containerWidth);
    const itemWidth = getItemWidth(containerWidth, column, gap.x);
    const itemHeight = getItemHeight(containerWidth, itemWidth);

    setColumns(column);
    setItemSize({
      width: itemWidth,
      height: itemHeight,
    });
  }, 200);

  useEffect(() => {
    if (!containerWidth) return;
    handleResize();
  }, [containerWidth]);

  useEffect(() => {
    rowVirtualizer.measure();
  }, [visibleCount]);

  return (
    <div ref={parentRef} className={wrapper} data-testid="wrapper">
      <VirtualWrapper
        data-testid="virtual-wrapper"
        style={{
          width: `${columnVirtualizer.getTotalSize()}px`,
          height: `${rowVirtualizer.getTotalSize()}px`,
        }}
      >
        {rowVirtualizer.getVirtualItems().map(virtualRow => (
          <Row
            key={virtualRow.index}
            ref={rowVirtualizer.measureElement}
            $width={columnVirtualizer.getTotalSize()}
            $translateY={virtualRow.start}
            $columns={columns}
            $itemWidth={itemSize.width}
            $gapX={gap.x}
          >
            {columnVirtualizer.getVirtualItems().map(virtualColumn => {
              const itemIndex =
                virtualRow.index * columns + virtualColumn.index;
              if (itemIndex >= visibleItems.length) return null;

              return (
                <Item
                  data-index={itemIndex}
                  data-testid="item-wrapper"
                  key={itemIndex}
                  $colStart={virtualColumn.index + 1}
                  $width={itemSize.width}
                >
                  {renderItem(visibleItems[itemIndex])}
                </Item>
              );
            })}
          </Row>
        ))}
      </VirtualWrapper>

      {visibleCount < items.length && (
        <Container justify="center">
          <Button
            variant="secondary"
            onClick={() => {
              setVisibleCount(currentCount => {
                const newCount = incrementBy
                  ? Math.min(currentCount + incrementBy, items.length)
                  : items.length;

                const lastRowIndex = Math.ceil(newCount / columns) - 1;

                setTimeout(() => {
                  rowVirtualizer.scrollToIndex(lastRowIndex, { align: 'end' });
                }, 0);

                return newCount;
              });
            }}
          >
            {t`showMore`}
          </Button>
        </Container>
      )}
    </div>
  );
}

const wrapper = css`
  width: 100%;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  position: relative;
`;

const VirtualWrapper = styled('div')`
  position: relative;
`;

const Row = styled('div')<{
  $width: number;
  $translateY: number;
  $columns: number;
  $itemWidth: number;
  $gapX: number;
}>`
  position: absolute;
  top: 0;
  left: 0;
  display: grid;
  width: ${p => p.$width}px;
  transform: translateY(${p => p.$translateY}px);
  grid-template-columns: repeat(${p => p.$columns}, ${p => p.$itemWidth}px);
  column-gap: ${p => p.$gapX}px;
`;

const Item = styled('div')<{
  $colStart: number;
  $width: number;
}>`
  position: relative;
  display: block;
  grid-column-start: ${p => p.$colStart};
  width: ${p => p.$width}px;
`;

const Container = styled(Flexbox)`
  position: sticky;
  bottom: 0;
  z-index: 10;
  padding: 8px 0;
`;
